// src/pages/Billing.jsx
import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { createSubscription, createCreditTopup, getCreditSummary, switchPlanType, createAddonSubscription, adminGetConfig, listFirmInvoices, updateFirm, cancelSubscription, resumeSubscription } from '../api/kailux'
import ErrorMessage from '../components/ErrorMessage'
import BackButton from '../components/BackButton'
import { positivePrice } from '../utils/pricing'

import Icon from '../components/Icon'

import { T, alpha } from '../theme/theme'
// Fallback defaults — used only if the live config fetch fails. The real
// numbers live in Supabase (platform_config.seat_pricing) and are editable
// from Admin Board > Plan Management > Seat Pricing without a redeploy.
const DEFAULT_SEAT_PRICE = 500
const DEFAULT_AFS_CREDITS_PER_SEAT = 10
const DEFAULT_PAYG_AFS_PRICE = 295   // R/AFS — mirrors backend pricing_config.py PAY_PER_ENGAGEMENT_AFS_PRICE
const DEFAULT_PAYG_REVIEW_PRICE = 450 // R/review file — mirrors backend pricing_config.py PAY_PER_ENGAGEMENT_REVIEW_PRICE
const DEFAULT_PAYG_MGMT_PRICE = 150  // R/file — mirrors backend pricing_config.py PAY_PER_ENGAGEMENT_MGMT_PRICE
const DEFAULT_AFS_OVERAGE_TIERS = [
  { min:1, max:1,    rate:90 },
  { min:2, max:4,    rate:80 },
  { min:5, max:9,    rate:65 },
  { min:10, max:null, rate:55 },
]
const DEFAULT_MGMT_ACCOUNT_TIERS = [
  { min:1, max:1,    rate:150 },
  { min:2, max:4,    rate:130 },
  { min:5, max:9,    rate:110 },
  { min:10, max:null, rate:95 },
]

export function rateForSeats(tiers, seats) {
  const tier = tiers.find(t => t.max === null ? seats >= t.min : seats >= t.min && seats <= t.max)
  return tier ? tier.rate : tiers[tiers.length - 1].rate
}

export function R(n){ return `R${Math.round(n).toLocaleString('en-ZA')}` }

// positivePrice lives in utils/pricing.js (shared with Signup); re-exported so
// existing imports from this module keep working.
export { positivePrice }

function AnnualPlanStatus({ firm, pricing }) {
  const [autoRenew, setAutoRenew] = useState(firm.auto_renew_annual !== false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const isAdmin = firm.firm_role === 'admin'

  const seats = firm.user_count || 1
  const annualPool = pricing.afs_credits_per_seat * seats * 12
  const used = firm.annual_afs_used_ytd || 0
  const pct = annualPool > 0 ? Math.min(100, Math.round((used / annualPool) * 100)) : 0
  const renewalDate = new Date(firm.paid_through_date).toLocaleDateString('en-ZA',
    { day: 'numeric', month: 'long', year: 'numeric' })

  const toggleAutoRenew = async () => {
    if (!isAdmin || saving) return
    const next = !autoRenew
    setSaving(true); setError('')
    try {
      await updateFirm(firm.id, { auto_renew_annual: next })
      setAutoRenew(next)
    } catch (e) {
      setError('Could not update this — try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'16px',
      padding:'20px 24px', marginBottom:'28px' }}>
      <div style={{ fontSize:'12px', color:T.mt, marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.5px' }}>
        Your annual plan
      </div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:'10px', flexWrap:'wrap', gap:'8px' }}>
        <div style={{ fontSize:'14px', color:T.tx }}>
          {used} of {annualPool} AFS used this year
        </div>
        <div style={{ fontSize:'13px', color:T.mt }}>Renews {renewalDate}</div>
      </div>
      <div style={{ height:'8px', borderRadius:'999px', background:T.dm, overflow:'hidden', marginBottom:'16px' }}>
        <div style={{ height:'100%', width:`${pct}%`, borderRadius:'999px',
          background: pct >= 100 ? T.rd : T.or, transition:'width .3s' }} />
      </div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'10px' }}>
        <div style={{ fontSize:'13px', color:T.mt, maxWidth:'440px' }}>
          {autoRenew
            ? `We'll charge you again for another annual cycle on ${renewalDate} at your current seat count — same rate as this year.`
            : `Your plan switches to monthly billing after ${renewalDate} — you won't be charged for another annual cycle.`}
        </div>
        <button onClick={toggleAutoRenew} disabled={!isAdmin || saving}
          title={!isAdmin ? 'Only a firm admin can change this' : ''}
          style={{ padding:'8px 16px', borderRadius:'8px', border:`1px solid ${T.bd}`,
            background: autoRenew ? T.gnd : T.rdd, color: autoRenew ? T.gn : T.rd,
            fontWeight:'700', fontSize:'13px', cursor: isAdmin ? 'pointer' : 'not-allowed',
            opacity: saving ? 0.6 : 1, whiteSpace:'nowrap' }}>
          {saving ? 'Saving…' : autoRenew ? 'Auto-renew: On' : 'Auto-renew: Off'}
        </button>
      </div>
      {error && <div style={{ color:T.rd, fontSize:'12px', marginTop:'8px' }}>{error}</div>}
    </div>
  )
}

function CancelSubscriptionSection({ firm }) {
  const [confirming, setConfirming] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [accessUntil, setAccessUntil] = useState('')
  // cancellation_requested_at is on the firm object from useAuth's context —
  // reflects the true current state without needing its own fetch. Local
  // state below only tracks what THIS session just did, so the UI updates
  // instantly without waiting for a full context refresh.
  const [justCancelled, setJustCancelled] = useState(false)
  const [justResumed, setJustResumed] = useState(false)

  const isAdmin = firm.firm_role === 'admin'
  const cancellationPending = justResumed ? false : (justCancelled || !!firm.cancellation_requested_at)

  const isAnnual = firm.billing_frequency === 'annual'
  const renewalDate = firm.paid_through_date
    ? new Date(firm.paid_through_date).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })
    : null

  const handleCancel = async () => {
    if (!isAdmin || saving) return
    setSaving(true); setError('')
    try {
      const res = await cancelSubscription()
      setAccessUntil(res?.data?.access_until || '')
      setJustCancelled(true)
      setConfirming(false)
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not cancel — try again, or contact support.')
    } finally {
      setSaving(false)
    }
  }

  const handleResume = async () => {
    if (!isAdmin || saving) return
    setSaving(true); setError('')
    try {
      await resumeSubscription()
      setJustResumed(true)
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not resume — try again, or contact support.')
    } finally {
      setSaving(false)
    }
  }

  if (cancellationPending) {
    return (
      <div style={{ background:T.rdd, border:`1px solid ${alpha(T.rd, 0.19)}`, borderRadius:'16px',
        padding:'20px 24px', marginBottom:'28px' }}>
        <div style={{ fontSize:'14px', color:T.tx, marginBottom:'10px' }}>
          Your subscription is cancelled. {accessUntil
            ? `You'll keep full access until ${accessUntil}, then it will end.`
            : isAnnual && renewalDate
              ? `You'll keep full access until ${renewalDate}, then it will end.`
              : "You'll keep full access through your current billing cycle, then it will end."}
          {' '}You won't be charged again.
        </div>
        <button onClick={handleResume} disabled={!isAdmin || saving}
          title={!isAdmin ? 'Only a firm admin can change this' : ''}
          style={{ padding:'8px 16px', borderRadius:'8px', border:`1px solid ${T.bd}`,
            background:T.gnd, color:T.gn, fontWeight:'700', fontSize:'13px',
            cursor: isAdmin ? 'pointer' : 'not-allowed', opacity: saving ? 0.6 : 1 }}>
          {saving ? 'Resuming…' : 'Resume subscription'}
        </button>
        {error && <div style={{ color:T.rd, fontSize:'12px', marginTop:'8px' }}>{error}</div>}
      </div>
    )
  }

  return (
    <div style={{ textAlign:'center', marginBottom:'28px' }}>
      {!confirming ? (
        <button onClick={() => setConfirming(true)} disabled={!isAdmin}
          title={!isAdmin ? 'Only a firm admin can cancel the subscription' : ''}
          style={{ background:'none', border:'none', color:T.mt, fontSize:'13px',
            textDecoration:'underline', cursor: isAdmin ? 'pointer' : 'not-allowed' }}>
          Cancel subscription
        </button>
      ) : (
        <div style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'16px',
          padding:'20px 24px', display:'inline-block', maxWidth:'480px' }}>
          <div style={{ fontSize:'14px', color:T.tx, marginBottom:'14px' }}>
            {isAnnual && renewalDate
              ? `You'll keep full access until ${renewalDate} (the year you've already paid for), then your subscription will end — no partial refund, but nothing lost either.`
              : "You'll keep full access through your current billing cycle — one final invoice for anything you've used, same as always — then your subscription will end."}
          </div>
          <div style={{ display:'flex', gap:'10px', justifyContent:'center' }}>
            <button onClick={() => setConfirming(false)} disabled={saving}
              style={{ padding:'8px 16px', borderRadius:'8px', border:`1px solid ${T.bd}`,
                background:T.s2, color:T.tx, fontWeight:'700', fontSize:'13px', cursor:'pointer' }}>
              Never mind
            </button>
            <button onClick={handleCancel} disabled={saving}
              style={{ padding:'8px 16px', borderRadius:'8px', border:`1px solid ${alpha(T.rd, 0.19)}`,
                background:T.rdd, color:T.rd, fontWeight:'700', fontSize:'13px',
                cursor:'pointer', opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Cancelling…' : 'Yes, cancel'}
            </button>
          </div>
          {error && <div style={{ color:T.rd, fontSize:'12px', marginTop:'10px' }}>{error}</div>}
        </div>
      )}
    </div>
  )
}

export function addonPrice(addon) {
  if (addon.price_per_client_monthly !== undefined) return { amount: addon.price_per_client_monthly, suffix: '/client/mo' }
  if (addon.price_per_report !== undefined)         return { amount: addon.price_per_report, suffix: '/report' }
  if (addon.price_per_export !== undefined)         return { amount: addon.price_per_export, suffix: '/export' }
  return { amount: 0, suffix: '' }
}

export function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return n + (s[(v - 20) % 10] || s[v] || s[0])
}

export default function Billing() {
  const [seats,    setSeats]    = useState(1)
  const [loading,  setLoading]  = useState(false)
  const [error,    setError]    = useState('')
  // Billing cycle: 'monthly' or 'annual'. No discount (see pricing_config.py
  // module docstring — deliberate, held off until real usage-vs-cost data
  // justifies one). Annual firms instead get an ANNUAL (not monthly-reset)
  // pooled AFS allowance and a guaranteed best-tier overage/management-
  // accounts rate. Locked in on first successful payment, same as
  // billingDay — changeable only via re-subscribing (or toggling
  // auto-renew off, see the Manage plan section below).
  const [billingCycle, setBillingCycle] = useState('monthly')
  // Billing day: free choice at first subscribe, locked afterwards. Default
  // to today's day-of-month so there's always a sane pre-selected value.
  const [billingDay, setBillingDay] = useState(new Date().getDate())

  const [pricing, setPricing] = useState({
    seat_price: DEFAULT_SEAT_PRICE,
    afs_credits_per_seat: DEFAULT_AFS_CREDITS_PER_SEAT,
    afs_overage_tiers: DEFAULT_AFS_OVERAGE_TIERS,
    management_account_tiers: DEFAULT_MGMT_ACCOUNT_TIERS,
    pay_per_engagement_afs_price: DEFAULT_PAYG_AFS_PRICE,
    pay_per_engagement_mgmt_price: DEFAULT_PAYG_MGMT_PRICE,
    pay_per_engagement_review_price: DEFAULT_PAYG_REVIEW_PRICE,
  })

  const [addons,        setAddons]        = useState({})
  const [addonsLoading, setAddonsLoading] = useState(true)
  const [addonLoadingKey, setAddonLoadingKey] = useState(null)
  const [addonError,    setAddonError]    = useState('')

  const [invoices,        setInvoices]        = useState([])
  const [invoicesLoading, setInvoicesLoading] = useState(true)

  const { firm, user, loadFirm }  = useAuth()
  const navigate  = useNavigate()
  const location  = useLocation()
  // Signup (Sep 2026 — free trial removed): a brand-new firm lands here
  // straight after registration with the choice to pay now or later. This
  // flag only changes the header copy and shows a "Skip for now" escape
  // hatch to /onboarding — it does not change any actual billing behaviour;
  // an 'unpaid' firm that skips can use everything except AFS generation
  // (see is_generation_locked in main.py) until it comes back and subscribes.
  const isPostSignup = new URLSearchParams(location.search).get('postSignup') === '1'

  const SEAT_PRICE = pricing.seat_price
  const subscription = SEAT_PRICE * seats
  const afsPool       = pricing.afs_credits_per_seat * seats
  const overageRate   = rateForSeats(pricing.afs_overage_tiers, seats)
  const mgmtRate      = rateForSeats(pricing.management_account_tiers, seats)
  // Annual: full 12x price, no discount. The value is the annual pool +
  // best-tier overage/mgmt rate below, not a lower subscription price.
  const annualTotal        = subscription * 12
  const annualAfsPool      = afsPool * 12
  const bestAfsOverageRate  = rateForSeats(pricing.afs_overage_tiers, 10)
  const bestMgmtRate        = rateForSeats(pricing.management_account_tiers, 10)

  useEffect(() => {
    adminGetConfig('seat_pricing')
      .then(res => {
        const cfg = res.config_value
        if (cfg && cfg.seat_price) setPricing({
          ...cfg,
          pay_per_engagement_afs_price:  positivePrice(cfg.pay_per_engagement_afs_price,  DEFAULT_PAYG_AFS_PRICE),
          pay_per_engagement_mgmt_price: positivePrice(cfg.pay_per_engagement_mgmt_price, DEFAULT_PAYG_MGMT_PRICE),
          pay_per_engagement_review_price: positivePrice(cfg.pay_per_engagement_review_price, DEFAULT_PAYG_REVIEW_PRICE),
        })
      })
      .catch(() => { /* keep defaults — Billing still fully usable */ })
  }, [])

  useEffect(() => {
    if (!firm?.id) { setInvoicesLoading(false); return }
    listFirmInvoices(firm.id)
      .then(d => setInvoices(d.invoices || []))
      .catch(() => { /* invoice history is best-effort — not blocking */ })
      .finally(() => setInvoicesLoading(false))
  }, [firm?.id])

  useEffect(() => {
    adminGetConfig('addons')
      .then(res => setAddons(res.config_value || {}))
      .catch(() => setAddonError('Could not load add-ons.'))
      .finally(() => setAddonsLoading(false))
  }, [])

  const firmAddonsEnabled = firm?.addons_enabled || []

  const submitToPayFast = (data) => {
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = data.payfast_url

    Object.entries(data).forEach(([key, val]) => {
      if (key === 'payfast_url') return
      const input = document.createElement('input')
      input.type  = 'hidden'
      input.name  = key
      input.value = val
      form.appendChild(input)
    })

    document.body.appendChild(form)
    form.submit()
  }

  const handleEnableAddon = async (addonKey) => {
    setAddonLoadingKey(addonKey); setAddonError('')
    try {
      const { data } = await createAddonSubscription(addonKey)
      submitToPayFast(data)
    } catch (err) {
      setAddonError(err?.response?.data?.detail || 'Could not connect to payment gateway. Please try again.')
      setAddonLoadingKey(null)
    }
  }

  const handleSubscribe = async () => {
    setLoading(true); setError('')
    try {
      const { data } = await createSubscription({
        firm_id:    firm?.id,
        firm_email: firm?.email,
        firm_name:  firm?.name,
        plan:       String(seats),
        billing:    billingCycle,
        billing_day: firm?.billing_day || billingDay,
      })
      submitToPayFast(data)
    } catch (err) {
      setError('Could not connect to payment gateway. Please try again.')
      setLoading(false)
    }
  }

  const adjustSeats = (delta) => setSeats(s => Math.max(1, s + delta))

  // Prepaid credits (pay-per-engagement): counts by type, bought by quantity. Counts and
  // prices come from the backend.
  const [credit, setCredit] = useState(null)
  const [qty, setQty] = useState({ afs: 1, review: 1, mgmt: 1, audit: 1 })
  const loadCredit = async () => {
    try { const { data } = await getCreditSummary(); setCredit(data) } catch { /* card shows defaults */ }
  }
  useEffect(() => {
    if (firm?.plan_type === 'pay_per_engagement') loadCredit()
  }, [firm?.plan_type])

  const creditMax = credit?.max_per_purchase || 100
  const clampQty = (v) => Math.max(1, Math.min(creditMax, Math.floor(Number(v)) || 1))
  const handleBuy = async (type) => {
    setLoading(true); setError('')
    try {
      const { data } = await createCreditTopup(type, clampQty(qty[type]))
      submitToPayFast(data)
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not connect to payment gateway. Please try again.')
      setLoading(false)
    }
  }

  const [switchingPlan, setSwitchingPlan] = useState(false)
  const [switchError,   setSwitchError]   = useState('')
  // A billed pay-per-engagement firm moving to monthly has to pay for it, so the
  // switch just reveals the normal subscription checkout (the plan only changes
  // once PayFast confirms the payment). Abandoning it leaves the firm unchanged.
  const [wantsMonthlyCheckout, setWantsMonthlyCheckout] = useState(false)
  const handleSwitchPlanType = async () => {
    const target = firm?.plan_type === 'pay_per_engagement' ? 'subscription' : 'pay_per_engagement'
    const label  = target === 'pay_per_engagement' ? 'pay-per-engagement' : 'the monthly subscription'
    const billed = firm?.subscription_status === 'active'
    const prompt = !billed
      ? `Switch this firm to ${label}? You haven't been charged yet, so this is free to change.`
      : target === 'subscription'
        ? 'Switch to the monthly subscription? You will choose your seats and pay the first month on the next step. Your plan only changes once that payment goes through.'
        : "Switch to pay-per-engagement? It takes effect immediately and each AFS is then charged to your card when you generate it. The monthly subscription fee you've already paid is not refunded, and unused monthly credits don't carry over."
    if (!window.confirm(prompt)) return
    setSwitchingPlan(true); setSwitchError('')
    try {
      const res = await switchPlanType(firm.id, target)
      if (res?.data?.requires_checkout) { setWantsMonthlyCheckout(true); return }
      if (user?.id) await loadFirm(user.id)
    } catch (err) {
      setSwitchError(err?.response?.data?.detail || 'Could not switch plans. Please try again or contact support@kailux.co.za.')
    } finally {
      setSwitchingPlan(false)
    }
  }

  return (
    <div style={{ minHeight:'100vh', background:T.bg,
      fontFamily:"'Outfit',sans-serif", color:T.tx,
      padding:'60px 40px', position:'relative', overflow:'hidden' }}>

      <div style={{ position:'fixed', top:'20%', left:'50%',
        transform:'translateX(-50%)', width:'800px', height:'400px',
        pointerEvents:'none', zIndex:0,
        background:`radial-gradient(ellipse,${alpha(T.or, 0.07)} 0%,transparent 70%)` }}/>

      <div style={{ maxWidth:'720px', margin:'0 auto', position:'relative', zIndex:1 }}>

        {/* Header */}
        <div style={{ textAlign:'center', marginBottom:'40px' }}>
          <div style={{ display:'flex', justifyContent:'center', marginBottom:'20px' }}>
            <BackButton onClick={() => navigate('/dashboard')} label="Back to Dashboard"
              textColor={T.mt} borderColor={T.bd} accentColor={T.or} />
          </div>
          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'34px',
            fontWeight:'800', letterSpacing:'-1px', marginBottom:'10px' }}>
            {isPostSignup ? "You're almost there." : 'One plan. Every feature. Priced per user.'}
          </div>
          <div style={{ fontSize:'15px', fontWeight:'400', color:T.mt, maxWidth:'520px',
            margin:'0 auto 0', lineHeight:'1.7' }}>
            {isPostSignup
              ? "Subscribe now to start generating AFS right away, or skip for now — you can explore the rest of KaiLux and subscribe whenever you're ready."
              : 'Every user gets full access to the whole platform — no feature tiers.'}
          </div>
          {isPostSignup && (
            <button onClick={() => navigate('/onboarding')}
              style={{ marginTop:'16px', padding:'9px 18px', borderRadius:'8px',
                border:`1px solid ${T.bd}`, background:'transparent', color:T.mt,
                fontFamily:"'Outfit',sans-serif", fontSize:'13px', fontWeight:'600', cursor:'pointer' }}>
              Skip for now — I'll pay later
            </button>
          )}
        </div>

        {firm?.plan_type === 'pay_per_engagement' && !wantsMonthlyCheckout ? (
          <div style={{ background:T.s1, border:`1px solid ${T.bd}`,
            borderRadius:'16px', padding:'32px', marginBottom:'28px' }}>
            <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'20px', fontWeight:'800', marginBottom:'20px' }}>
              Pay-per-engagement
            </div>
            {(() => {
              const counts = credit?.credits || {}
              const prices = credit?.prices || {
                afs: pricing.pay_per_engagement_afs_price,
                review: pricing.pay_per_engagement_review_price,
                mgmt: pricing.pay_per_engagement_mgmt_price,
                audit: null,
              }
              const rowsDef = [
                { key: 'afs',    label: 'AFS' },
                { key: 'review', label: 'Review' },
                { key: 'mgmt',   label: 'Management accounts' },
                { key: 'audit',  label: 'Audit' },
              ].filter(r => r.key !== 'audit' || (prices.audit != null && counts.audit != null))
              return (
                <div style={{ display:'grid', gap:'12px', marginBottom:'8px' }}>
                  {rowsDef.map(r => {
                    const unit = Number(prices[r.key])
                    const n = clampQty(qty[r.key])
                    return (
                      <div key={r.key} style={{ display:'flex', alignItems:'center', gap:'16px', flexWrap:'wrap',
                        background:T.s2, borderRadius:'12px', padding:'16px 20px' }}>
                        <div style={{ minWidth:'170px' }}>
                          <div style={{ fontSize:'14px', fontWeight:'700' }}>{r.label}</div>
                          <div style={{ fontSize:'12px', color:T.mt }}>{`${R(unit)} each`}</div>
                        </div>
                        <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'28px', fontWeight:'900', color:T.or, minWidth:'56px' }}
                          aria-label={`${r.label} credits held`}>
                          {Number(counts[r.key] ?? 0)}
                        </div>
                        <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap' }}>
                          <input type="number" min={1} max={creditMax} value={qty[r.key]}
                            aria-label={`${r.label} quantity`}
                            onChange={e => setQty(q => ({ ...q, [r.key]: e.target.value }))}
                            style={{ padding:'10px 12px', borderRadius:'10px', border:`1px solid ${T.bd}`,
                              background:T.s1, color:T.tx, fontFamily:"'Outfit',sans-serif", fontSize:'14px', width:'80px' }} />
                          <span style={{ fontSize:'14px', fontWeight:'700', minWidth:'90px', textAlign:'right' }}>{R(unit * n)}</span>
                          <button onClick={() => handleBuy(r.key)} disabled={loading}
                            style={{ padding:'10px 22px', borderRadius:'10px', border:'none',
                              background: loading ? T.mt : T.prg, color:T.onpri,
                              fontFamily:"'Outfit',sans-serif", fontSize:'14px', fontWeight:'800',
                              cursor: loading ? 'not-allowed' : 'pointer' }}>
                            {loading ? 'Redirecting to PayFast...' : 'Buy'}
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )
            })()}
            {error && <div style={{ marginTop:'12px' }}><ErrorMessage>{error}</ErrorMessage></div>}
            {(credit?.ledger || []).length > 0 && (
              <div style={{ marginTop:'24px' }}>
                <div style={{ fontSize:'13px', fontWeight:'700', marginBottom:'8px' }}>Recent activity</div>
                {credit.ledger.slice(0, 8).map((row, i) => (
                  <div key={row.id || i} style={{ display:'flex', justifyContent:'space-between', gap:'12px',
                    fontSize:'13px', padding:'8px 0', borderBottom:`1px solid ${T.bd}` }}>
                    <span style={{ color:T.mt }}>
                      {(row.created_at || '').slice(0, 10)} · {row.description || row.kind}
                    </span>
                    <span style={{ color: Number(row.quantity) >= 0 ? T.gn : T.tx, fontWeight:'700' }}>
                      {`${Number(row.quantity) >= 0 ? '+' : '-'}${Math.abs(Number(row.quantity))}`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
        <>
        {/* Current annual plan status + auto-renew toggle — only shown for
            firms actually on an active annual plan right now. */}
        {firm?.subscription_status === 'active' && firm?.billing_frequency === 'annual' && firm?.paid_through_date && (
          <AnnualPlanStatus firm={firm} pricing={pricing} />
        )}

        {/* Cancel / resume subscription — shown for ANY active subscription
            (monthly or annual), and also while a cancellation is already
            pending, regardless of billing_frequency. See kailux-master-
            plan.md §4 item — this fixes "Cancel anytime" having no actual
            way to do it. */}
        {wantsMonthlyCheckout && (
          <div style={{ textAlign:'center', marginBottom:'18px' }}>
            <button onClick={() => setWantsMonthlyCheckout(false)}
              style={{ background:'none', border:'none', color:T.mt, cursor:'pointer',
                fontFamily:"'Outfit',sans-serif", fontSize:'13px', textDecoration:'underline' }}>
              ← Stay on pay-per-engagement
            </button>
          </div>
        )}
        {!wantsMonthlyCheckout && (firm?.subscription_status === 'active' || firm?.cancellation_requested_at) && (
          <CancelSubscriptionSection firm={firm} />
        )}

        {/* Billing cycle toggle */}
        <div style={{ display:'flex', justifyContent:'center', gap:'10px', marginBottom:'20px' }}>
          <button onClick={() => setBillingCycle('monthly')}
            style={{ padding:'10px 20px', borderRadius:'10px',
              border:`1px solid ${billingCycle==='monthly' ? T.pri : T.bd}`,
              background: billingCycle==='monthly' ? T.prg : T.s2,
              color: billingCycle==='monthly' ? T.onpri : T.tx,
              fontWeight:'700', fontSize:'14px', cursor:'pointer' }}>
            Monthly
          </button>
          <button onClick={() => setBillingCycle('annual')}
            style={{ padding:'10px 20px', borderRadius:'10px',
              border:`1px solid ${billingCycle==='annual' ? T.pri : T.bd}`,
              background: billingCycle==='annual' ? T.or : T.s2,
              color: billingCycle==='annual' ? T.onpri : T.tx,
              fontWeight:'700', fontSize:'14px', cursor:'pointer',
              display:'flex', alignItems:'center', gap:'8px' }}>
            Annual
            <span style={{ fontSize:'11px', fontWeight:'800', padding:'2px 7px', borderRadius:'999px',
              background: billingCycle==='annual' ? T.onpri : T.pri,
              color: billingCycle==='annual' ? T.pri : T.onpri }}>
              annual AFS pool
            </span>
          </button>
        </div>
        {billingCycle === 'annual' && (
          <div style={{ textAlign:'center', fontSize:'13px', color:T.mt, marginBottom:'20px', maxWidth:'480px', marginLeft:'auto', marginRight:'auto' }}>
            {R(annualTotal)} billed once a year — same total you'd pay monthly, with two real
            upgrades: your {annualAfsPool}-AFS allowance is pooled across the whole year instead
            of resetting every month, and any extra AFS beyond that are charged at our best rate,
            R{bestAfsOverageRate} each, regardless of your seat count. Management accounts are
            also always billed at our best rate (R{bestMgmtRate}/file) on the annual plan.
          </div>
        )}

        {/* Seat stepper + summary card */}
        <div style={{ background:T.s1, border:`1px solid ${T.bd}`,
          borderRadius:'16px', padding:'32px', marginBottom:'28px' }}>

          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
            marginBottom:'24px' }}>
            <div>
              <div style={{ fontSize:'13px', color:T.mt, marginBottom:'4px' }}>Team size</div>
              <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'20px', fontWeight:'800' }}>
                How many users?
              </div>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:'14px' }}>
              <button onClick={() => adjustSeats(-1)} disabled={seats<=1}
                style={{ width:'36px', height:'36px', borderRadius:'10px', border:`1px solid ${T.bd}`,
                  background:T.s2, color:T.tx, fontSize:'18px', fontWeight:'700',
                  cursor: seats<=1 ? 'not-allowed' : 'pointer', opacity: seats<=1 ? 0.4 : 1 }}>−</button>
              <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'28px', fontWeight:'900',
                minWidth:'40px', textAlign:'center' }}>{seats}</div>
              <button onClick={() => adjustSeats(1)}
                style={{ width:'36px', height:'36px', borderRadius:'10px', border:`1px solid ${T.bd}`,
                  background:T.s2, color:T.tx, fontSize:'18px', fontWeight:'700', cursor:'pointer' }}>+</button>
            </div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px', marginBottom:'20px' }}>
            <div style={{ background:T.s2, borderRadius:'12px', padding:'16px' }}>
              <div style={{ fontSize:'12px', color:T.mt, marginBottom:'6px' }}>
                {billingCycle === 'annual' ? 'Annual subscription' : 'Monthly subscription'}
              </div>
              <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'26px', fontWeight:'900', color:T.or }}>
                {billingCycle === 'annual' ? R(annualTotal) : R(subscription)}
              </div>
              <div style={{ fontSize:'12px', color:T.mt, marginTop:'4px' }}>
                {billingCycle === 'annual'
                  ? `${seats} user${seats>1?'s':''} × ${R(SEAT_PRICE)} × 12mo`
                  : `${seats} user${seats>1?'s':''} × ${R(SEAT_PRICE)}`}
              </div>
            </div>
            <div style={{ background:T.s2, borderRadius:'12px', padding:'16px' }}>
              <div style={{ fontSize:'12px', color:T.mt, marginBottom:'6px' }}>
                {billingCycle === 'annual' ? 'AFS credits / year' : 'AFS credits / month'}
              </div>
              <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'26px', fontWeight:'900', color:T.gn }}>
                {billingCycle === 'annual' ? annualAfsPool : afsPool}
              </div>
              <div style={{ fontSize:'12px', color:T.mt, marginTop:'4px' }}>
                {billingCycle === 'annual'
                  ? 'Pooled for the full year · rolls over, pauses if unpaid'
                  : 'Pooled across the firm · rolls over, pauses if unpaid'}
              </div>
            </div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
            <div style={{ fontSize:'13px', color:T.lt, display:'flex', justifyContent:'space-between',
              borderTop:`1px solid ${T.bd}`, paddingTop:'12px' }}>
              <span style={{ color:T.mt }}>Extra AFS beyond pool</span>
              <span>{R(billingCycle === 'annual' ? bestAfsOverageRate : overageRate)} each</span>
            </div>
            <div style={{ fontSize:'13px', color:T.lt, display:'flex', justifyContent:'space-between',
              borderTop:`1px solid ${T.bd}`, paddingTop:'12px' }}>
              <span style={{ color:T.mt }}>Management accounts</span>
              <span>{R(billingCycle === 'annual' ? bestMgmtRate : mgmtRate)} / file</span>
            </div>
          </div>

          <div style={{ fontSize:'12px', color:T.mt, marginTop:'16px', lineHeight:'1.6' }}>
            Extra AFS and management account files are billed in arrears on next month's invoice —
            shown as separate line items, never a surprise charge, never chased separately.
          </div>

          {/* Billing day — free choice before first subscribe, locked afterwards */}
          <div style={{ borderTop:`1px solid ${T.bd}`, marginTop:'20px', paddingTop:'20px' }}>
            {firm?.billing_day ? (
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'8px' }}>
                <div style={{ fontSize:'13px', color:T.tx }}>
                  <Icon name="calendar" /> You're billed on the <strong>{ordinal(firm.billing_day)}</strong> of every month.
                </div>
                <div style={{ fontSize:'12px', color:T.mt }}>
                  To change this date, contact <a href="mailto:support@kailux.co.za" style={{ color:T.or }}>support@kailux.co.za</a>
                </div>
              </div>
            ) : (
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'12px' }}>
                <div>
                  <div style={{ fontSize:'13px', color:T.tx, marginBottom:'2px' }}>
                    Choose your billing day
                  </div>
                  <div style={{ fontSize:'12px', color:T.mt }}>
                    This is a one-time choice — changing it later means contacting support.
                  </div>
                </div>
                <select
                  value={billingDay}
                  onChange={e => setBillingDay(Number(e.target.value))}
                  style={{ background:T.s2, border:`1px solid ${T.bd}`, color:T.tx,
                    borderRadius:'8px', padding:'8px 12px', fontSize:'14px',
                    fontFamily:"'Outfit',sans-serif", cursor:'pointer' }}>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                    <option key={day} value={day}>{ordinal(day)} of the month</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Full feature list — same for every seat count */}
        <div style={{ background:T.s1, border:`1px solid ${T.bd}`,
          borderRadius:'16px', padding:'28px 32px', marginBottom:'28px' }}>
          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'16px', fontWeight:'800', marginBottom:'14px' }}>
            Included for every user, at every team size
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px 20px' }}>
            {[
              'Trial balance upload + AI account mapping',
              'Bank statement processing',
              'Full IFRS for SMEs financial statements',
              '22 mandatory notes — AI drafted & editable',
              'Expert Review dashboard + going concern flags',
              'Workpapers engine + partner sign-off',
              'Group consolidations & multi-entity',
              'iXBRL regulatory export',
              'Benchmarking / Business Intelligence reports',
              'Client portal + OTP-verified sign-off',
              'Practice workflow — intake, compliance calendar, batch processing',
              'Unlimited clients',
            ].map((f, i) => (
              <div key={i} style={{ display:'flex', gap:'8px', alignItems:'flex-start', fontSize:'13px' }}>
                <span style={{ color:T.gn, flexShrink:0, fontWeight:'700', fontSize:'11px', marginTop:'2px' }}><Icon name="check" /></span>
                <span style={{ color:T.lt }}>{f}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Subscribe CTA */}
        <div style={{ background:`${alpha(T.or, 0.03)}`, border:`1px solid ${alpha(T.or, 0.19)}`,
          borderRadius:'16px', padding:'28px 32px',
          display:'flex', alignItems:'center',
          justifyContent:'space-between', flexWrap:'wrap', gap:'20px' }}>
          <div>
            <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'18px',
              fontWeight:'800', marginBottom:'4px' }}>
              {billingCycle === 'annual'
                ? `${R(annualTotal)}/year for ${seats} user${seats>1?'s':''}`
                : `${R(subscription)}/month for ${seats} user${seats>1?'s':''}`}
            </div>
            <div style={{ fontSize:'14px', color:T.mt }}>
              {billingCycle === 'annual' ? annualAfsPool : afsPool} AFS included · {R(billingCycle === 'annual' ? bestAfsOverageRate : overageRate)} per extra
            </div>
          </div>

          <div style={{ display:'flex', flexDirection:'column', gap:'8px', alignItems:'flex-end' }}>
            {error && (
              <ErrorMessage>
                {error}
              </ErrorMessage>
            )}
            <button onClick={handleSubscribe} disabled={loading}
              style={{ padding:'14px 36px', borderRadius:'12px', border:'none',
                background: loading ? T.mt : T.prg,
                color:T.onpri, fontFamily:"'Outfit',sans-serif",
                fontSize:'15px', fontWeight:'800',
                cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: loading ? 'none' : `0 6px 28px ${alpha(T.pri, 0.4)}`,
                display:'flex', alignItems:'center', gap:'8px', transition:'all 0.3s' }}>
              {loading ? (
                <>
                  <span style={{ width:'16px', height:'16px',
                    border:`2px solid ${alpha(T.onpri, 0.3)}`,
                    borderTopColor:T.onpri, borderRadius:'50%',
                    animation:'spin 0.8s linear infinite', display:'inline-block' }}/>
                  Redirecting to PayFast...
                </>
              ) : 'Subscribe Now →'}
            </button>
            <div style={{ fontSize:'12px', color:T.mt, textAlign:'right' }}>
              <Icon name="lock" /> Secure payment via PayFast · Cancel anytime
            </div>
          </div>
        </div>
        </>
        )}

        {/* Pre-payment plan switch (Oct 2026) — only while nothing has ever
            been charged (subscription_status === 'unpaid'); once a firm has
            gone active even once, the backend itself refuses the switch and
            points to support instead, so this stays available regardless of
            which plan card rendered above. */}
        {(firm?.subscription_status === 'unpaid' || (firm?.subscription_status === 'active' && !wantsMonthlyCheckout)) && (
          <div style={{ background:T.s2, border:`1px dashed ${T.bd}`, borderRadius:'14px',
            padding:'18px 22px', marginBottom:'28px', display:'flex', alignItems:'center',
            justifyContent:'space-between', flexWrap:'wrap', gap:'12px' }}>
            <div style={{ fontSize:'13px', color:T.mt, maxWidth:'480px' }}>
              {firm?.plan_type === 'pay_per_engagement'
                ? "Realized a monthly subscription suits your volume better? "
                : "Not sure this plan fits your engagement volume? "}
              {firm?.subscription_status === 'unpaid'
                ? "You haven't been charged yet, so switching is free."
                : firm?.plan_type === 'pay_per_engagement'
                  ? 'You can switch any time. You pay for the first month on the next step.'
                  : 'You can switch any time once nothing is owing for this billing cycle.'}
              {switchError && <div style={{ color:T.rd, marginTop:'6px' }}>{switchError}</div>}
            </div>
            <button onClick={handleSwitchPlanType} disabled={switchingPlan}
              style={{ padding:'10px 20px', borderRadius:'10px', border:`1.5px solid ${T.bd}`,
                background:T.s1, color:T.tx, fontFamily:"'Outfit',sans-serif",
                fontSize:'13px', fontWeight:'700', whiteSpace:'nowrap',
                cursor: switchingPlan ? 'not-allowed' : 'pointer', opacity: switchingPlan ? 0.6 : 1 }}>
              {switchingPlan ? 'Switching...' : firm?.plan_type === 'pay_per_engagement'
                ? 'Switch to monthly subscription →' : 'Switch to pay-per-engagement →'}
            </button>
          </div>
        )}

        {/* Add-ons (excluding management accounts, which now lives in the summary card above) */}
        {!addonsLoading && Object.keys(addons).length > 0 && (
          <div style={{ marginTop:'44px' }}>
            <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'22px',
              fontWeight:'800', letterSpacing:'-0.5px', marginBottom:'6px', textAlign:'center' }}>
              Add-ons
            </div>
            <div style={{ fontSize:'14px', color:T.mt, textAlign:'center', marginBottom:'20px' }}>
              Optional extras you can enable any time — billed monthly via PayFast.
            </div>

            {addonError && (
              <div style={{ fontSize:'13px', color:T.rd, background:T.rdd,
                padding:'8px 14px', borderRadius:'7px', textAlign:'center',
                border:`1px solid ${alpha(T.rd, 0.2)}`, marginBottom:'16px' }}>
                {addonError}
              </div>
            )}

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'16px' }}>
              {Object.entries(addons).filter(([key]) => key !== 'management_accounts').map(([key, addon]) => {
                const { amount, suffix } = addonPrice(addon)
                const alreadyEnabled = firmAddonsEnabled.includes(key)
                const disabledState  = addon.active === false || alreadyEnabled || amount <= 0
                const isThisLoading  = addonLoadingKey === key

                let ctaLabel = `Enable — R${amount.toLocaleString('en-ZA')}${suffix}`
                if (addon.active === false) ctaLabel = 'Unavailable'
                else if (alreadyEnabled)    ctaLabel = <><Icon name="check" /> Enabled</>
                else if (amount <= 0)       ctaLabel = 'Contact support'

                return (
                  <div key={key} style={{ background:T.s1, border:`1px solid ${T.bd}`,
                    borderRadius:'14px', padding:'18px 20px' }}>
                    <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'15px',
                      fontWeight:'800', marginBottom:'4px' }}>
                      {addon.label || key}
                    </div>
                    <div style={{ fontSize:'12px', color:T.mt, marginBottom:'12px',
                      lineHeight:'1.6', minHeight:'32px' }}>
                      {addon.description || ''}
                    </div>
                    <div style={{ fontSize:'13px', color:T.tx, marginBottom:'14px' }}>
                      {amount > 0 ? <>R{amount.toLocaleString('en-ZA')}<span style={{ color:T.mt }}>{suffix}</span></> : '—'}
                    </div>
                    <button
                      onClick={() => !disabledState && handleEnableAddon(key)}
                      disabled={disabledState || isThisLoading}
                      style={{ width:'100%', padding:'10px', borderRadius:'9px', border:'none',
                        background: alreadyEnabled ? T.gnd
                          : disabledState ? T.dm
                          : T.prg,
                        color: alreadyEnabled ? T.gn : disabledState ? T.mt : T.onpri,
                        fontFamily:"'Outfit',sans-serif", fontSize:'13px', fontWeight:'700',
                        cursor: disabledState || isThisLoading ? 'not-allowed' : 'pointer' }}>
                      {isThisLoading ? 'Redirecting to PayFast...' : ctaLabel}
                    </button>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Invoice history */}
        {!invoicesLoading && (
          <div style={{ marginTop:'44px' }}>
            <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'22px',
              fontWeight:'800', letterSpacing:'-0.5px', marginBottom:'6px', textAlign:'center' }}>
              Invoice History
            </div>
            <div style={{ fontSize:'14px', color:T.mt, textAlign:'center', marginBottom:'20px' }}>
              Every past invoice — one charge per month, broken down line by line.
            </div>
            {invoices.length === 0 ? (
              <div style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'14px',
                padding:'24px', textAlign:'center', fontSize:'13px', color:T.mt }}>
                No invoices yet — your first will appear here after your first billing cycle.
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
                {invoices.map(inv => (
                  <div key={inv.id} style={{ background:T.s1, border:`1px solid ${T.bd}`,
                    borderRadius:'14px', padding:'18px 20px' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px' }}>
                      <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'15px', fontWeight:'800' }}>{inv.period}</div>
                      <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                        <span style={{ fontSize:'11px', fontWeight:700, padding:'3px 10px', borderRadius:'6px',
                          background: inv.status==='paid' ? T.gnd : T.ord,
                          color: inv.status==='paid' ? T.gn : T.or, textTransform:'uppercase' }}>
                          {inv.status}
                        </span>
                        <span style={{ fontFamily:"'Syne',sans-serif", fontSize:'16px', fontWeight:'900' }}>{R(inv.total_zar)}</span>
                      </div>
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:'3px' }}>
                      {(inv.charges?.line_items || []).map((li, i) => (
                        <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:'12px', color:T.mt }}>
                          <span>{li.label} — {li.detail}</span>
                          <span>{R(li.amount)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Trust bar */}
        <div style={{ display:'flex', justifyContent:'center', gap:'24px',
          flexWrap:'wrap', fontSize:'13px', color:T.mt, marginTop:'24px' }}>
          {[<><Icon name="lock" /> 256-bit SSL</>,<><Icon name="card" /> Processed by PayFast</>,
            <><Icon name="pin" /> South African company</>,<><Icon name="check" /> POPIA compliant</>,
            <><Icon name="noCalendar" /> Cancel anytime</>].map((t, i) => (
            <span key={i}>{t}</span>
          ))}
        </div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )
}
